import { useState } from 'react';
import { tokenManager } from '../config/authConfig';
import API_CIFRA from '../config/Variables_apis';


const useIncidencias = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Función para obtener headers de autenticación
  const getAuthHeaders = () => {
    const token = tokenManager.getAccessToken();
    const headers = {
      "Content-Type": "application/json",
    };
    
    if (token && !tokenManager.isTokenExpired()) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    return headers;
  };

  // Función para manejar errores específicos
  const handleApiError = (error, response) => {
    let errorMessage = "Error al crear la incidencia";
    
    if (response?.status === 400) {
      errorMessage = "Datos inválidos. Verifica la información ingresada.";
    } else if (response?.status === 401) {
      errorMessage = "Error de autenticación. Intenta nuevamente.";
    } else if (response?.status === 403) {
      errorMessage = "No tienes permisos para crear incidencias.";
    } else if (response?.status === 404) {
      errorMessage = "Recurso no encontrado.";
    } else if (response?.status === 500) {
      errorMessage = "Error interno del servidor. Intenta más tarde.";
    }
    
    return errorMessage;
  };

  // Función para crear incidencia
  const crearIncidencia = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      // Validar que se proporcione al menos una forma de ubicación
      const tieneCoordenadas = (formData.latitud || formData.latitude) && (formData.longitud || formData.longitude);
      const tieneVia = formData.fk_via_id;
      
      if (!tieneCoordenadas && !tieneVia) {
        throw new Error('Debe proporcionar ubicación: coordenadas del mapa O seleccionar una vía específica.');
      }
      
      if (tieneCoordenadas && tieneVia) {
        throw new Error('Debe elegir solo una forma de ubicación: coordenadas del mapa O vía específica, no ambas.');
      }

      // Determinar endpoint según si es anónimo o no
      const endpoint = formData.isAnonymous 
        ? API_CIFRA.incidencia.crearAnonima 
        : API_CIFRA.incidencia.crear;
      
      // Preparar datos según los campos reales del backend
      const dataToSend = {
        // Campos de ubicación
        inc_deLatitud: formData.latitud || formData.latitude,
        inc_deLongitud: formData.longitud || formData.longitude,
        inc_chDireccion: formData.direccion || formData.address,
        inc_chReferencia: formData.referencia,
        
        // Campos de distrito y ciudad (extraídos automáticamente)
        inc_chDistric: formData.distrito,
        inc_chCity: formData.ciudad,
        
        // Campos de fecha y hora del incidente
        inc_dtFechaIncidente: formData.fecha,
        inc_tmHoraIncidente: formData.hora,
        
        // Días de la semana (solo incluir si están seleccionados)
        
        // Descripción del incidente
        
        // Categoría (relación)
        fk_categoria_id: formData.categoriaId,
        
        // Campo anónimo
        inc_boAnonimo: formData.isAnonymous,
        
        // Vía específica (si se seleccionó)
        fk_via_id: formData.fk_via_id,
      };

      // Agregar campos opcionales condicionalmente
      if (formData.fechaFin) dataToSend.inc_dtFechaFin = formData.fechaFin;
      if (formData.horaFin) dataToSend.inc_tmHoraFin = formData.horaFin;
      
      // Campo opcional: inc_txComentario
      if (formData.comentario) dataToSend.inc_txComentario = formData.comentario;
      
      // Agregar días de la semana si están seleccionados
      if (formData.lunes) dataToSend.inc_chLunes = "LUNES";
      if (formData.martes) dataToSend.inc_chMartes = "MARTES";
      if (formData.miercoles) dataToSend.inc_chMiercoles = "MIERCOLES";
      if (formData.jueves) dataToSend.inc_chJueves = "JUEVES";
      if (formData.viernes) dataToSend.inc_chViernes = "VIERNES";
      if (formData.sabado) dataToSend.inc_chSabado = "SABADO";
      if (formData.domingo) dataToSend.inc_chDomingo = "DOMINGO";

      // Si es anónimo, usar alias en inc_chNombre
      if (formData.isAnonymous) {
        dataToSend.inc_chNombre = formData.alias || formData.nombres || "Anónimo";
        // Limpiar datos personales para anónimos
        dataToSend.inc_chNumDoc = null;
        dataToSend.inc_chApePat = null;
        dataToSend.inc_chApeMat = null;
        dataToSend.inc_chGenero = null;
        if (formData.telefono) dataToSend.inc_chTelefono = formData.telefono;
        if (formData.email) dataToSend.inc_chEmail = formData.email;
      } else {
        // Si NO es anónimo, agregar datos personales
        dataToSend.inc_chNumDoc = formData.documento || null;
        dataToSend.inc_chNombre = formData.nombres;
        dataToSend.inc_chApePat = formData.apellidoPaterno;
        dataToSend.inc_chApeMat = formData.apellidoMaterno;
        dataToSend.inc_chGenero = formData.genero;
        if (formData.telefono) dataToSend.inc_chTelefono = formData.telefono;
        if (formData.email) dataToSend.inc_chEmail = formData.email;
      }

      // Limpiar datos undefined y strings vacíos antes de enviar
      const cleanData = {};
      Object.keys(dataToSend).forEach(key => {
        const value = dataToSend[key];
        if (value !== undefined && value !== null && value !== '') {
          cleanData[key] = value;
        }
      });

      console.log('Datos a enviar:', cleanData); // Debug

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanData),
      });

      const responseData = await response.json();

      if (!response.ok) {
        const errorMessage = handleApiError(error, response);
        throw new Error(responseData.error || responseData.detail || errorMessage);
      }

      return { 
        success: true, 
        data: responseData,
        ticket: responseData.inc_chTiket,
        incidenciaId: responseData.inc_inId
      };

    } catch (error) {
      const errorMessage = error.message || "Error al crear la incidencia";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función para buscar incidencia por ticket
  const buscarIncidenciaPorTicket = async (ticket) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_CIFRA.incidencia.buscarPorTicket}?ticket=${ticket}`, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = handleApiError(error, response);
        throw new Error(data.error || data.detail || errorMessage);
      }

      return { success: true, data };

    } catch (error) {
      const errorMessage = error.message || "Error al buscar la incidencia";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Función para obtener detalles de incidencia
  const obtenerDetalleIncidencia = async (incidenciaId) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_CIFRA.incidencia.detallePublico(incidenciaId), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMessage = handleApiError(error, response);
        throw new Error(data.error || data.detail || errorMessage);
      }

      return { success: true, data };

    } catch (error) {
      const errorMessage = error.message || "Error al obtener detalles de la incidencia";
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  return {
    // Estado
    loading,
    error,
    
    // Funciones
    crearIncidencia,
    buscarIncidenciaPorTicket,
    obtenerDetalleIncidencia,
  };
};

export default useIncidencias; 